import { FileBadge, ShieldAlert, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { useCertificate, useCertificateMutations } from '@/features/certificates/hooks';
import { ExpirationBar } from './ExpirationBar';

interface Props {
  cnpjId: string;
  cnpjLabel: string;
  onUpload: () => void;
}

function formatCnpj(raw: string): string {
  const d = raw.replace(/\D/g, '').padStart(14, '0');
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

export function CertificateCard({ cnpjId, cnpjLabel, onUpload }: Props) {
  const query = useCertificate(cnpjId);
  const { remove } = useCertificateMutations(cnpjId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <FileBadge className="h-5 w-5 text-(--color-accent)" aria-hidden />
            {cnpjLabel}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={onUpload}>
            <Upload className="mr-1 h-4 w-4" aria-hidden />
            {query.data ? 'Substituir' : 'Enviar certificado'}
          </Button>
        </div>
      </CardHeader>

      {query.isLoading ? (
        <p className="text-sm text-(--color-fg-muted)">Carregando…</p>
      ) : query.data ? (
        <div className="flex flex-col gap-3">
          <dl className="grid grid-cols-3 gap-2 text-sm">
            <dt className="col-span-1 text-(--color-fg-muted)">Titular</dt>
            <dd className="col-span-2 break-words">{query.data.holderName}</dd>

            <dt className="col-span-1 text-(--color-fg-muted)">CNPJ</dt>
            <dd className="col-span-2">{formatCnpj(query.data.holderCnpj)}</dd>

            <dt className="col-span-1 text-(--color-fg-muted)">Expira em</dt>
            <dd className="col-span-2">
              {new Date(query.data.expiresAt).toLocaleDateString('pt-BR')}
            </dd>
          </dl>

          <ExpirationBar daysToExpire={query.data.daysToExpire} />

          <div className="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (confirm('Remover o certificado deste CNPJ?')) remove.mutate();
              }}
              disabled={remove.isPending}
            >
              Remover
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 rounded-md border border-dashed border-(--color-border) p-4">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-(--color-error)" aria-hidden />
          <div className="flex-1 text-sm">
            <p className="font-medium">Nenhum certificado</p>
            <p className="text-(--color-fg-muted)">
              Upload obrigatório para emitir NF neste CNPJ.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
